import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { primaryText, iconBgGreen } from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { PieChart } from 'lucide-react';
import { RevenueMix } from '../../../utils/revenueHistoryCalculations';

interface RevenueMixCardProps {
  revenueMix: RevenueMix;
  loading?: boolean;
  /** Passed through to StatCard — see its compact phone layout doc. */
  mobileLayout?: 'row' | 'tile';
}

const foodColor = '#18B081';
const drinksColor = '#F4B857';

const RevenueMixCard: React.FC<RevenueMixCardProps> = ({ revenueMix, loading = false, mobileLayout }) => {
  const { t } = useTranslation();

  const mixBar = (
    <div className="flex flex-col gap-2">
      {/* Horizontal bar chart */}
      <div className="w-full h-6 flex rounded-md overflow-hidden">
        {revenueMix.food.percentage > 0 && (
          <div
            style={{ width: `${revenueMix.food.percentage}%`, backgroundColor: foodColor }}
            className="h-full"
          />
        )}
        {revenueMix.drinks.percentage > 0 && (
          <div
            style={{ width: `${revenueMix.drinks.percentage}%`, backgroundColor: drinksColor }}
            className="h-full"
          />
        )}
      </div>

      {/* Labels and percentages */}
      <div className="flex justify-between items-center">
        {revenueMix.food.percentage > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: foodColor }} />
            <span className="text-[#667085] dark:text-textTertiary-dark text-[14px] leading-[21px] font-medium">
              {t('revenueHistory.food')}
            </span>
            <span className={cn('text-[14px] leading-[21px] font-medium', primaryText)}>
              {revenueMix.food.percentage.toFixed(1)}%
            </span>
          </div>
        )}
        {revenueMix.drinks.percentage > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: drinksColor }} />
            <span className="text-[#667085] dark:text-textTertiary-dark text-[14px] leading-[21px] font-medium">
              {t('revenueHistory.drinks')}
            </span>
            <span className={cn('text-[14px] leading-[21px] font-medium', primaryText)}>
              {revenueMix.drinks.percentage.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <StatCard
      icon={<PieChart className="w-6 h-6" style={{ color: '#079455' }} />}
      iconBg={iconBgGreen}
      title={t('revenueHistory.revenueMix')}
      value=""
      loading={loading}
      bottom={mixBar}
      mobileLayout={mobileLayout}
    />
  );
};

export default RevenueMixCard;
